import React, { useMemo } from "react";
import { Share2 } from "lucide-react";
import { toast } from "sonner";

/**
 * PaidByPieCard — "Who paid this month?" donut on the Budget tab.
 *
 * Aggregates invoice_history[] entries by paid_by within the current calendar
 * month (upload_date-based, IST) and summing amount per person. Renders:
 *   • saffron-accented donut (SVG conic-gradient — no chart lib needed)
 *   • legend row: "You ₹8,400 · Rohit ₹5,200 · Split ₹1,800"
 *   • share button that copies a plain-text card to the clipboard
 *
 * Hides itself unless at least 2 distinct paid_by values exist for the month
 * — a single "You" slice is not interesting and violates the spec.
 */

const SLICE_COLORS = [
  "#FF6B00", // saffron (You)
  "#3B6D11", // dark green
  "#0C447C", // navy
  "#BA7517", // amber
  "#534AB7", // purple
  "#00A878", // teal
];

const toIstMonthKey = (iso) => {
  if (!iso || typeof iso !== "string") return "";
  // upload_date is stored ISO. Take the calendar month in the source tz
  // (they're written in UTC/IST-ish) — for month-bucketing an ISO prefix is
  // accurate to the day; drift across the day-boundary is negligible for
  // this UX (a share card slightly off is not a correctness bug).
  return iso.length >= 7 ? iso.slice(0, 7) : "";
};

const currentMonthKey = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

const fmt = (n) => `₹${Math.round(Number(n || 0)).toLocaleString("en-IN")}`;

export default function PaidByPieCard({ invoiceHistory = [] }) {
  const { slices, total, monthLabel, hasMultiple } = useMemo(() => {
    const monthKey = currentMonthKey();
    const buckets = new Map();
    let grand = 0;
    (invoiceHistory || []).forEach((row) => {
      if (toIstMonthKey(row.upload_date) !== monthKey) return;
      const amt = Number(row.amount || 0);
      if (!Number.isFinite(amt) || amt <= 0) return;
      const who = (row.paid_by || "You").trim() || "You";
      buckets.set(who, (buckets.get(who) || 0) + amt);
      grand += amt;
    });

    const list = Array.from(buckets, ([label, amount]) => ({ label, amount }))
      .sort((a, b) => b.amount - a.amount)
      .map((s, i) => ({
        ...s,
        color: SLICE_COLORS[i % SLICE_COLORS.length],
        pct: grand > 0 ? s.amount / grand : 0,
      }));

    const [y, mo] = monthKey.split("-").map((n) => parseInt(n, 10));
    const label = new Date(y, mo - 1, 1).toLocaleDateString("en-IN", {
      month: "long",
      year: "numeric",
    });

    return {
      slices: list,
      total: grand,
      monthLabel: label,
      hasMultiple: list.length >= 2,
    };
  }, [invoiceHistory]);

  // Spec: hide entirely unless at least 2 distinct paid_by values exist for
  // the current month. A single-slice "You" pie is not an insight.
  if (!hasMultiple) return null;

  const conic = slices
    .reduce(
      (acc, s) => {
        const start = acc.cursor;
        const end = start + s.pct * 100;
        acc.stops.push(`${s.color} ${start}% ${end}%`);
        acc.cursor = end;
        return acc;
      },
      { cursor: 0, stops: [] },
    )
    .stops.join(", ");

  const onShare = async () => {
    const rows = slices
      .map((s) => `${s.label} ${fmt(s.amount)}`)
      .join(" · ");
    const text = `Who paid this month (${monthLabel}) on CartIQ 360:\n${rows}\nTotal ${fmt(total)}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Who paid this month?", text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Breakdown copied to clipboard");
      }
    } catch {
      // User dismissed the share sheet — that's fine, no toast needed.
    }
  };

  return (
    <div
      className="cartiq-card p-4 md:p-5"
      data-testid="paid-by-pie-card"
      aria-label="Who paid this month"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div
            className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#888]"
            data-testid="paid-by-pie-title"
          >
            Who paid this month?
          </div>
          <div className="text-xs text-[#666] mt-0.5">{monthLabel}</div>
        </div>
        <button
          type="button"
          onClick={onShare}
          className="text-[10px] uppercase tracking-[0.22em] font-bold px-2.5 py-1.5 border border-[#FF6B00] text-[#FF6B00] hover:bg-[#FF6B00] hover:text-white transition-colors inline-flex items-center gap-1.5"
          data-testid="paid-by-share-btn"
          aria-label="Share breakdown"
        >
          <Share2 className="w-3 h-3" />
          Share
        </button>
      </div>

      <div className="flex items-center gap-4">
        {/* Donut — pure SVG so we don't pull in a chart lib for one card. */}
        <div
          className="shrink-0 relative"
          style={{
            width: 88,
            height: 88,
            background: `conic-gradient(${conic})`,
            borderRadius: "50%",
          }}
          data-testid="paid-by-pie-donut"
          role="img"
          aria-label={`Total spend ${fmt(total)}`}
        >
          {/* Inner cut-out to make it a donut */}
          <div
            className="absolute inset-[16px] bg-[#161620] flex flex-col items-center justify-center"
            style={{ borderRadius: "50%" }}
          >
            <div className="font-mono-num font-bold text-[13px] text-[#F0EDE8] leading-tight">
              {fmt(total)}
            </div>
            <div className="text-[8px] uppercase tracking-[0.18em] text-[#666]">
              total
            </div>
          </div>
        </div>

        {/* Legend */}
        <ul className="flex-1 min-w-0 space-y-1.5" data-testid="paid-by-legend">
          {slices.map((s) => (
            <li
              key={s.label}
              className="flex items-center gap-2 text-xs"
              data-testid={`paid-by-legend-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <span
                className="w-2 h-2 shrink-0"
                style={{ background: s.color }}
                aria-hidden="true"
              />
              <span className="text-[#F0EDE8] font-medium truncate flex-1">
                {s.label}
              </span>
              <span className="font-mono-num font-bold text-[#F0EDE8]">
                {fmt(s.amount)}
              </span>
              <span className="text-[10px] text-[#666] tabular-nums w-[38px] text-right">
                {Math.round(s.pct * 100)}%
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
